import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  listarClientesTodos,
  cadastrarClienteZenGestao,
  atualizarClienteZenGestao,
  obterClienteZenGestao,
  atualizarModulosLicenca,
} from "../services/api";
import { Search, Save } from "lucide-react";
import SeletorModulosLicenca from "./common/SeletorModulosLicenca";
import { montarItensModulos, quantidadeModuloPorCodigo } from "../utils/modulosLicenca";
import ZenGestaoEmpresasSection from "./ZenGestaoEmpresasSection";

const slugify = (text) =>
  (text || "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const FormularioZenGestao = ({ token }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = id !== undefined;

  const [clientes, setClientes] = useState([]);
  const [clientesOriginais, setClientesOriginais] = useState([]);

  const [selectedClientId, setSelectedClientId] = useState("");
  const [slug, setSlug] = useState("");
  const [licencaNomeFantasia, setLicencaNomeFantasia] = useState("");

  const [busca, setBusca] = useState("");

  const [loading, setLoading] = useState(true);
  const [loadingLicenca, setLoadingLicenca] = useState(false);
  const [error, setError] = useState(null);
  const [selecaoModulos, setSelecaoModulos] = useState({
    selectedIds: [],
    quantidades: {},
    catalogo: [],
  });
  const [empresasReloadKey, setEmpresasReloadKey] = useState(0);

  const empresasAdicionaisLimite = useMemo(
    () => quantidadeModuloPorCodigo(selecaoModulos, "ADD_EMPRESA"),
    [selecaoModulos]
  );

  // ====== clientes (igual ZenTag) ======
  useEffect(() => {
    const fetchClientes = async () => {
      try {
        const resp = await listarClientesTodos(token, {
          pageSize: 2000,
          sortBy: "razaosocial",
        });

        const data = resp?.data ?? resp;
        const arr = Array.isArray(data)
          ? data
          : Array.isArray(data?.items)
          ? data.items
          : Array.isArray(data?.Items)
          ? data.Items
          : [];

        setClientes(arr);
        setClientesOriginais(arr);
      } catch (err) {
        setError("Erro ao carregar clientes.");
        console.error("Erro ao carregar clientes:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchClientes();
  }, [token]);

  // ====== licença (edit) ======
  useEffect(() => {
    if (!isEditMode) return;

    const fetchLicenca = async () => {
      setLoadingLicenca(true);
      try {
        const response = await obterClienteZenGestao(id, token);
        const lic = response?.data;

        const clienteId = lic?.clienteId ?? lic?.ClienteId;
        if (clienteId) {
          setSelectedClientId(String(clienteId));
        } else if (lic?.cnpj || lic?.CNPJ) {
          const cnpjLic = (lic.cnpj || lic.CNPJ || "").replace(/\D/g, "");
          const match = (clientesOriginais || []).find(
            (c) => (c.cnpj || "").replace(/\D/g, "") === cnpjLic
          );
          if (match) setSelectedClientId(String(match.id));
        }
        setSlug(lic?.slug ?? "");
        setLicencaNomeFantasia(lic?.nomeFantasia ?? lic?.NomeFantasia ?? "");
      } catch (err) {
        setError("Erro ao carregar dados do ZenGestão.");
        console.error("Erro ao carregar dados do ZenGestão:", err);
      } finally {
        setLoadingLicenca(false);
      }
    };

    fetchLicenca();
  }, [id, isEditMode, token]);

  const clienteSelecionado = useMemo(() => {
    const idNum = Number(selectedClientId);
    const base = Array.isArray(clientesOriginais) ? clientesOriginais : [];
    return base.find((c) => c.id === idNum) || null;
  }, [selectedClientId, clientesOriginais]);

  const handleBuscar = (e) => {
    const termo = e.target.value;
    setBusca(termo);

    const t = termo.toLowerCase();
    const base = Array.isArray(clientesOriginais) ? clientesOriginais : [];
    const filtered = base.filter(
      (c) =>
        (c.cnpj || "").toLowerCase().includes(t) ||
        (c.razaoSocial || "").toLowerCase().includes(t) ||
        (c.nomeFantasia || "").toLowerCase().includes(t)
    );
    setClientes(filtered);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isEditMode) {
      if (!selectedClientId) {
        alert("Por favor, selecione um cliente.");
        return;
      }
      if (!clienteSelecionado) {
        alert("Cliente selecionado não encontrado.");
        return;
      }
    }

    const payload = isEditMode
      ? {
          nomeFantasia: licencaNomeFantasia || clienteSelecionado?.nomeFantasia || "",
          qtdPDVs: 1,
          slug,
          ativo: true,
        }
      : {
          clienteId: Number(selectedClientId),
          revendaId: clienteSelecionado?.revendaId ?? 0,
          nomeFantasia: clienteSelecionado?.nomeFantasia ?? "",
          qtdPDVs: 1,
          slug: slugify(slug),
          ativo: true,
        };

    if (!isEditMode && !slugify(slug)) {
      alert("Informe um slug válido (letras minúsculas, números e hífen).");
      return;
    }

    setLoadingLicenca(true);
    try {
      if (!Array.isArray(selecaoModulos.catalogo) || selecaoModulos.catalogo.length === 0) {
        alert("Aguarde o carregamento dos módulos antes de salvar.");
        return;
      }

      const itensModulos = montarItensModulos({
        ...selecaoModulos,
        licencaTipo: "ZenGestao",
      });

      if (isEditMode) {
        await atualizarClienteZenGestao(id, payload, token);
        await atualizarModulosLicenca("ZenGestao", Number(id), itensModulos, token);
        setEmpresasReloadKey((k) => k + 1);
        alert("ZenGestão atualizado com sucesso!");
      } else {
        const resp = await cadastrarClienteZenGestao(payload, token);
        const novoId = resp?.data?.id;
        if (novoId && itensModulos.length > 0) {
          await atualizarModulosLicenca("ZenGestao", novoId, itensModulos, token);
        }
        alert("ZenGestão cadastrado com sucesso!");
      }
      navigate("/licenciamento/zengestao/list");
    } catch (err) {
      const data = err?.response?.data;

      if (typeof data === "string") {
        alert(data);
      } else {
        const message =
          data?.message ||
          (data?.errors && Object.values(data.errors).flat().join("\n")) ||
          err.message ||
          "Erro ao salvar ZenGestão.";
        alert(message);
      }
      console.error(err);
    } finally {
      setLoadingLicenca(false);
    }
  };

  if (loading)
    return (
      <div className="p-6 text-center text-gray-600 dark:text-slate-200">
        Carregando clientes...
      </div>
    );

  if (error)
    return <div className="p-6 text-center text-red-500">{error}</div>;

  return (
    <div className="max-w-3xl mx-auto">
      <div className="zen-card rounded-2xl p-6 shadow-sm dark:backdrop-blur">
        {/* header */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
            {isEditMode ? "Editar ZenGestão" : "Nova Licença ZenGestão"}
          </h2>
          <p className="text-sm text-gray-500 dark:text-slate-300/80">
            {isEditMode
              ? "Edite os dados da licença (cliente travado)."
              : "Selecione um cliente existente e informe os dados da licença."}
          </p>
        </div>

        {/* seletor cliente */}
        <div className="zen-card rounded-2xl p-4 mb-6 dark:bg-white/5 dark:border-white/10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-400"
                size={18}
              />
              <input
                type="text"
                placeholder="Buscar cliente por CNPJ / Razão / Nome Fantasia"
                value={busca}
                onChange={handleBuscar}
                disabled={isEditMode}
                className="
                  pl-10 pr-4 py-2.5 w-full rounded-xl border outline-none transition
                  zen-card text-gray-900 placeholder:text-gray-400
                  focus:ring-2 focus:ring-blue-500/60
                  disabled:opacity-60 disabled:cursor-not-allowed
                  dark:bg-slate-950/40 dark:border-white/10 dark:text-white dark:placeholder:text-slate-400
                  dark:focus:ring-blue-400/40
                "
              />
            </div>

            <select
              className="
                w-full p-2.5 rounded-xl border outline-none transition
                zen-card border-gray-300 text-gray-900
                focus:ring-2 focus:ring-blue-500/60
                disabled:opacity-60 disabled:cursor-not-allowed
                dark:bg-slate-950/40 dark:border-white/10 dark:text-white
                dark:focus:ring-blue-400/40
              "
              value={selectedClientId}
              onChange={(e) => setSelectedClientId(e.target.value)}
              required
              disabled={isEditMode}
            >
              <option value="">-- Selecione um cliente --</option>
              {Array.isArray(clientes) &&
                clientes.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.razaoSocial} ({c.cnpj})
                  </option>
                ))}
            </select>
          </div>
        </div>

        {/* form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="zen-card rounded-2xl p-4 dark:bg-white/5 dark:border-white/10">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">
                  Slug
                </label>
                <input
                  value={slug}
                  onChange={(e) => setSlug(slugify(e.target.value))}
                  className="
                    w-full px-3 py-2.5 rounded-xl border outline-none transition
                    zen-card border-gray-300 text-gray-900
                    focus:ring-2 focus:ring-blue-500/60
                    dark:bg-slate-950/40 dark:border-white/10 dark:text-white
                    dark:focus:ring-blue-400/40
                    disabled:opacity-70 disabled:cursor-not-allowed
                  "
                  placeholder="ex: mercado-do-ze"
                  required
                  disabled={isEditMode}
                  autoComplete="off"
                  spellCheck={false}
                />
                <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">
                  {isEditMode
                    ? "Slug não pode ser alterado — define o banco do cliente."
                    : "Somente minúsculas, sem acento ou espaço. Define o banco do cliente."}
                </p>
              </div>
            </div>
            <p className="text-xs text-gray-500 dark:text-slate-400 mt-3">
              A licença inclui 1 PDV por padrão. PDVs adicionais e empresas são configurados nos módulos abaixo.
            </p>
          </div>

          <SeletorModulosLicenca
            token={token}
            licencaTipo="ZenGestao"
            licencaId={isEditMode ? Number(id) : null}
            selectedIds={selecaoModulos.selectedIds}
            quantidades={selecaoModulos.quantidades}
            onChange={(next) =>
              setSelecaoModulos((prev) => ({
                ...prev,
                ...next,
                catalogo: next.catalogo?.length ? next.catalogo : prev.catalogo,
              }))
            }
            disabled={loadingLicenca}
          />

          {isEditMode && (
            <ZenGestaoEmpresasSection
              token={token}
              zenGestaoId={Number(id)}
              empresasAdicionaisLimite={empresasAdicionaisLimite}
              reloadKey={empresasReloadKey}
            />
          )}

          {/* actions */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={loadingLicenca}
              className="
                inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition
                bg-blue-600 text-white hover:bg-blue-500
                disabled:opacity-60 disabled:cursor-not-allowed
              "
            >
              <Save size={18} />
              {loadingLicenca
                ? "Salvando..."
                : isEditMode
                ? "Salvar Alterações"
                : "Cadastrar no ZenGestão"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FormularioZenGestao;
